/**
 * How many columns fit, measured in rem rather than pixels.
 *
 * The rest of the stylesheet is rem for a reason (D62): a px breakpoint stops meaning
 * anything the moment someone changes their text size, and it fails silently — everything
 * looks right at the size it was built at. Measuring against the root font size keeps this
 * honest, so someone reading at 24px gets fewer, wider columns rather than three cramped
 * ones.
 *
 * `MIN_COLUMN` is the narrowest a card may be before another column is worth having. Below
 * it, the task rows start wrapping badly and two columns are worse than one.
 */

import { useEffect, useState } from "react";

import type { ColumnCount } from "./model";

/**
 * Narrowest usable card width, in rem.
 *
 * 24 was too eager: a task row carries a title, four badges and four buttons, and at 24rem
 * those wrap into three lines each. 34 was too greedy in the other direction — it wanted
 * 1088px before it would give a second column, which a tablet in landscape does not have,
 * so a device with room for two comfortable columns got one.
 *
 * 28 asks for 896px for two and 1344px for three. If this still feels wrong it is the one
 * number to change, and the column dividers can rebalance whatever it decides.
 */
export const MIN_COLUMN = 28;

/**
 * Never more than three *automatically*, however wide the monitor. Measure has limits that
 * width does not.
 */
export const MAX_COLUMNS = 3;

/**
 * The ceiling when you set it by hand.
 *
 * Twenty: roughly double what the largest screen anyone here owns can use well, which is
 * the point. Finding out that twenty columns is a mess takes one click to undo, and a limit
 * set at "what we think you should want" is a worse failure than a layout you chose and
 * disliked.
 *
 * It is a real number rather than a shrug because the page width follows the count — see
 * `.layout-grid` in styles.css. Without that, `.app.wide` would bind first and twenty
 * columns would be 98px each on any monitor at all.
 *
 * The high ceiling also buys something specific: a column dragged wide and left empty is a
 * hole in the layout, which on a maximised window is a place to put something else.
 */
export const MAX_MANUAL_COLUMNS = 20;

function rootFontSize(): number {
  const size = Number.parseFloat(window.getComputedStyle(document.documentElement).fontSize);
  return Number.isFinite(size) && size > 0 ? size : 16;
}

export function columnsFor(widthPx: number, remPx = 16): ColumnCount {
  const widthRem = widthPx / remPx;
  const fits = Math.floor(widthRem / MIN_COLUMN);
  return Math.min(Math.max(fits, 1), MAX_COLUMNS) as ColumnCount;
}

export function useColumns(ref: React.RefObject<HTMLElement | null>): ColumnCount {
  const [columns, setColumns] = useState<ColumnCount>(1);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const measure = () =>
      setColumns(columnsFor(element.getBoundingClientRect().width, rootFontSize()));

    measure();

    // ResizeObserver rather than a resize listener: the container narrows when a scrollbar
    // appears or the nav wraps, neither of which is a window resize.
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [ref]);

  return columns;
}
